import { useEffect, useState } from 'react';
import type { CommandeListee } from '../api/types';

/**
 * Saisie d'un numéro quand celui de la fiche est inutilisable.
 *
 * Le scénario sollicite le client ; s'il le fait par SMS, il lui faut un numéro
 * composable. La base ne porte que ce qui a été tapé à la main, et il s'y
 * trouve des « 06 12 34 » tronqués, des « à demander », des numéros nationaux
 * sur des commandes livrées à l'étranger — autant de sollicitations qui
 * partiraient dans le vide sans que rien ne le dise.
 *
 * **Le numéro enregistré est montré quand il existe**, et c'est le cœur de
 * cette fenêtre : sans lui, l'utilisateur ne peut ni comprendre pourquoi on lui
 * demande une saisie, ni s'en servir comme point de départ — la plupart du
 * temps il manque un chiffre, et c'est plus vite corrigé que retrouvé.
 *
 * Elle s'ouvre aussi quand il n'y a AUCUN numéro, et c'est un correctif : ce
 * cas laissait passer la demande, au motif que le scénario se rabattrait sur
 * le mail. Il ne le fait pas — la commande 39384 est partie avec `null` et n'a
 * joint personne. Sans numéro composable, la demande ne part pas.
 */
export function SaisirTelephoneAvis({
  commande, enAttente, envoiImmediat, onValider, onFermer,
}: {
  commande: CommandeListee;
  enAttente: boolean;
  /** L'échéance est déjà passée : cocher enverra dans la foulée. */
  envoiImmediat: boolean;
  onValider: (telephone: string) => void;
  onFermer: () => void;
}) {
  const { telephone } = commande.avis;
  const [saisi, setSaisi] = useState('');

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div
        className="box" role="dialog" aria-modal="true"
        aria-label={`Numéro du client pour ${commande.ref}`}
        style={{ borderTopColor: 'var(--brick)' }}
      >
        <div className="mhead">
          <h2>Numéro de téléphone à fournir</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <form
          className="mbody"
          id="form-telephone-avis"
          onSubmit={(e) => { e.preventDefault(); if (saisi.trim()) onValider(saisi.trim()); }}
        >
          <p className="err" style={{ marginBottom: 14 }}>
            {telephone.brut
              ? <>Le numéro enregistré ne peut pas être composé.{telephone.raison && <> {telephone.raison}</>}</>
              : <>Cette commande ne porte aucun numéro de téléphone.</>}
            {' '}La demande ne peut pas partir sans.
          </p>

          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>Client</dt><dd style={{ textAlign: 'left' }}>{commande.client}</dd>
            {/* Montré tel qu'il est en base : c'est ce qui explique la demande,
                et c'est presque toujours le bon numéro à un chiffre près. */}
            <dt>Numéro enregistré</dt>
            <dd style={{ textAlign: 'left' }}>
              {telephone.brut
                ? <span className="mono" style={{ color: 'var(--brick)' }}>{telephone.brut}</span>
                : <span style={{ color: 'var(--ink-3)' }}>aucun</span>}
            </dd>
            <dt>Pays de livraison</dt>
            <dd style={{ textAlign: 'left' }}>{commande.pays ?? 'non renseigné'}</dd>
          </dl>

          <label style={{ display: 'block', fontSize: 12.5, marginBottom: 6 }} htmlFor="tel-avis">
            Numéro à envoyer
          </label>
          <input
            id="tel-avis"
            type="tel"
            className="mono"
            autoFocus
            value={saisi}
            placeholder="+33 6 12 34 56 78"
            onChange={(e) => setSaisi(e.target.value)}
            style={{ width: '100%' }}
          />
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '8px 0 0' }}>
            L&apos;écriture est libre : l&apos;application met le numéro au format international.
            Un numéro local — commençant par un zéro — n&apos;est accepté que si le pays de
            livraison est connu ; sinon, écrivez-le avec son indicatif.
            {' '}
            Il ne sert qu&apos;à cette demande : la fiche client reste inchangée.
          </p>
        </form>

        <div className="mfoot">
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="submit" form="form-telephone-avis" className="btn pri"
            disabled={enAttente || !saisi.trim()}
          >
            {enAttente
              ? <><span className="spin" /> Enregistrement…</>
              : envoiImmediat ? 'Envoyer maintenant' : 'Programmer la demande'}
          </button>
        </div>
      </div>
    </div>
  );
}
